import type { ComputedRef, Ref } from "vue";
import type {
  PublicCampaign,
  PublicEvent,
  PublicPlan,
  PublicQRCode,
  PublicShop,
  PublicOrganization,
} from "~/composables/useRechercheData";
import { themeCanonique, foldSearchText } from "~/composables/useRechercheHelpers";

// Type importé de la SOURCE UNIQUE (`#shared/utils/listingTypes`) plutôt que
// redéclaré : cinq copies de cette union vivaient ici, et l'ajout du rayon
// « boutiques » (2026-08-17) les aurait fait diverger une à une.
import type { ValidType } from "#shared/utils/listingTypes";
import { parseDbInstant } from "@donoor/utils/dates";
import { slugTexte } from "@donoor/utils";
import { villeLieu, villeOrganisation } from "#shared/utils/villeAnnuaire";

// Champs optionnels de campagne utilisés côté filtrage (la catégorie n'est pas
// dans le type public de base mais renvoyée par l'API).
type CampaignWithCategory = PublicCampaign & { category?: string };

// ⚠️ Le TRI n'est PLUS fait ici. L'API (CampaignService.listPublic /
// listPublicOrganizations) trie TOUTE la base (vérifiée → montant → … pour les
// cagnottes ; total collecté pour les orgs) PUIS pagine. Avec l'infinite
// scroll, on ACCUMULE les pages dans l'ordre serveur : re-trier l'accumulation
// côté client ré-ordonnerait l'union (une page 3 pourrait remonter avant la
// page 1) et casserait la cohérence de la pagination. Ce composable ne fait
// donc plus que du FILTRAGE (catégorie/ville/prix/date) ; la recherche texte
// est poussée à l'API (useRechercheData) et re-filtrée ici en filet.

// Ville : même CLÉ que l'annuaire et l'API (lot 4) — `villeOrganisation` /
// `villeLieu` (#shared/utils/villeAnnuaire), homonymes séparés
// (« saint-denis » ≠ « saint-denis-974 »). Plus de slug local : il divergeait
// du SQL sur les lettres rares (ø, ł, İ).

export interface UseRechercheFilteringDeps {
  activeType: ComputedRef<ValidType> | Ref<ValidType>;
  campaigns: Ref<PublicCampaign[]>;
  events: Ref<PublicEvent[]>;
  adhesions: Ref<PublicPlan[]>;
  qrCodes: Ref<PublicQRCode[]>;
  organizations: Ref<PublicOrganization[]>;
  shops: Ref<PublicShop[]>;
  effectiveSearchQuery: ComputedRef<string>;
  /** Thèmes choisis (vide = tous) : un résultat en a L'UN. */
  selectedCategories: Ref<string[]>;
  /** Types d'activité (événements) choisis, vide = tous. */
  selectedActivityTypes: Ref<string[]>;
  selectedVille: Ref<string | null>;
  priceMin: Ref<number | null>;
  priceMax: Ref<number | null>;
  dateFrom: Ref<string | null>;
  dateTo: Ref<string | null>;
}

/**
 * useRechercheFiltering — application client-side des filtres (texte,
 * catégorie, ville, prix, date) sur les 6 collections de /recherche.
 * Extrait 1:1 de `pages/recherche/[[type]].vue` (Zone A split >800 lignes).
 */
export function useRechercheFiltering(deps: UseRechercheFilteringDeps) {
  const {
    activeType,
    campaigns,
    events,
    adhesions,
    qrCodes,
    organizations,
    shops,
    effectiveSearchQuery,
    selectedCategories,
    selectedActivityTypes,
    selectedVille,
    priceMin,
    priceMax,
    dateFrom,
    dateTo,
  } = deps;

  /** Thème retenu ? (aucun thème choisi = tous ; valeur ancienne traduite) */
  const themeRetenu = (category: string | null | undefined) => {
    const choix = selectedCategories.value;
    if (choix.length === 0) return true;
    const t = themeCanonique(category);
    return !!t && choix.includes(t);
  };
  const typeRetenu = (type: string | null | undefined) =>
    selectedActivityTypes.value.length === 0 ||
    (!!type && selectedActivityTypes.value.includes(type));

  const filteredCampaigns = computed(() => {
    let result = campaigns.value;
    if (selectedCategories.value.length > 0 && activeType.value === "cagnottes") {
      result = result.filter(
        (c) => themeRetenu((c as CampaignWithCategory).category),
      );
    }
    if (effectiveSearchQuery.value) {
      const q = foldSearchText(effectiveSearchQuery.value);
      result = result.filter(
        (c) =>
          foldSearchText(c.title).includes(q) ||
          foldSearchText(c.organization_name).includes(q) ||
          foldSearchText(c.description ?? "").includes(q),
      );
    }
    // Ordre serveur préservé (tri global vérifiée→montant→… fait par l'API).
    return result;
  });

  const filteredEvents = computed(() => {
    let result = events.value;
    if (selectedCategories.value.length > 0 && activeType.value === "evenements") {
      result = result.filter((e) => themeRetenu(e.category));
    }
    if (selectedActivityTypes.value.length > 0 && activeType.value === "evenements") {
      result = result.filter((e) => typeRetenu(e.activity_type));
    }
    // Filtre ville : clé du lieu (`location_city_slug`, à défaut la ville
    // saisie) — même égalité que l'API ; les listings programmatiques
    // /evenements/[ville] restent la voie SEO canonique.
    if (selectedVille.value && activeType.value === "evenements") {
      const slug = selectedVille.value;
      result = result.filter((e) => villeLieu(e)?.cle === slug);
    }
    // Filtre date (start_date entre dateFrom et dateTo inclus).
    if ((dateFrom.value || dateTo.value) && activeType.value === "evenements") {
      const from = dateFrom.value ? new Date(dateFrom.value).getTime() : null;
      // dateTo : on prend fin de journée (23:59:59) pour inclure les events
      // qui démarrent ce jour-là.
      const to = dateTo.value
        ? new Date(dateTo.value).getTime() + 24 * 60 * 60 * 1000 - 1
        : null;
      result = result.filter((e) => {
        if (!e.start_date) return false;
        // Chaîne naïve de la base = UTC (sinon décalage selon l'écran).
        const t = parseDbInstant(e.start_date).getTime();
        if (from != null && t < from) return false;
        if (to != null && t > to) return false;
        return true;
      });
    }
    if (effectiveSearchQuery.value) {
      const q = foldSearchText(effectiveSearchQuery.value);
      result = result.filter(
        (e) =>
          foldSearchText(e.title).includes(q) ||
          foldSearchText(e.organization_name).includes(q),
      );
    }
    return result;
  });

  const filteredAdhesions = computed(() => {
    let result = adhesions.value;
    if (selectedCategories.value.length > 0 && activeType.value === "adhesions") {
      result = result.filter((p) => themeRetenu(p.category));
    }
    // Filtre tranche de prix : on prend le MIN du plan_group (cohérent
    // avec l'affichage « À partir de XX € »). Fallback parseFloat(p.price)
    // si l'API publique n'expose pas encore min_price.
    if ((priceMin.value != null || priceMax.value != null) && activeType.value === "adhesions") {
      const min = priceMin.value;
      const max = priceMax.value;
      result = result.filter((p) => {
        const n = p.min_price ?? parseFloat(p.price);
        if (!Number.isFinite(n)) return false;
        if (min != null && n < min) return false;
        if (max != null && n > max) return false;
        return true;
      });
    }
    if (effectiveSearchQuery.value) {
      const q = foldSearchText(effectiveSearchQuery.value);
      result = result.filter(
        (p) =>
          foldSearchText(p.title).includes(q) ||
          foldSearchText(p.organization_name).includes(q) ||
          foldSearchText(p.description ?? "").includes(q),
      );
    }
    return result;
  });

  const filteredQRCodes = computed(() => {
    if (!effectiveSearchQuery.value) return qrCodes.value;
    const q = foldSearchText(effectiveSearchQuery.value);
    return qrCodes.value.filter(
      (qr) =>
        foldSearchText(qr.title ?? "").includes(q) ||
        foldSearchText(qr.organization_name).includes(q) ||
        foldSearchText(qr.description ?? "").includes(q),
    );
  });

  /**
   * Boutiques — thème (depuis 2026-09-26, hérité de l'association) et
   * recherche. La recherche et le thème sont déjà appliqués par l'API en mode
   * rayon ; ce filtre sert de filet et au mode « tout ». Pas de ville ici :
   * une boutique n'a pas d'adresse propre, l'API filtre par celle de son
   * association.
   */
  const filteredShops = computed(() => {
    let result = shops.value;
    if (selectedCategories.value.length > 0 && activeType.value === "boutiques") {
      result = result.filter((shop) => themeRetenu(shop.category));
    }
    if (!effectiveSearchQuery.value) return result;
    const q = foldSearchText(effectiveSearchQuery.value);
    return result.filter(
      (shop) =>
        foldSearchText(shop.title ?? "").includes(q) ||
        foldSearchText(shop.description ?? "").includes(q),
    );
  });

  const filteredOrganizations = computed(() => {
    let result = organizations.value;
    if (selectedCategories.value.length > 0 && activeType.value === "associations") {
      // Thème canonique (une ancienne valeur en base est traduite).
      result = result.filter(
        (o) => themeRetenu(o.category),
      );
    }
    // Filtre ville : clé de l'annuaire (`address_city_slug`, à défaut
    // l'analyseur unique sur full_address). Sans ville lisible, dernier
    // recours : la clé figure dans l'adresse (même filet que l'API).
    if (selectedVille.value && activeType.value === "associations") {
      const slug = selectedVille.value;
      result = result.filter((o) => {
        const ville = villeOrganisation(o);
        if (ville) return ville.cle === slug;
        return slugTexte(o.full_address).includes(slug);
      });
    }
    if (effectiveSearchQuery.value) {
      const q = foldSearchText(effectiveSearchQuery.value);
      result = result.filter(
        (o) =>
          foldSearchText(o.name).includes(q) ||
          foldSearchText(o.description ?? "").includes(q) ||
          foldSearchText(o.objet_social ?? "").includes(q),
      );
    }
    return result;
  });

  return {
    filteredCampaigns,
    filteredEvents,
    filteredAdhesions,
    filteredQRCodes,
    filteredShops,
    filteredOrganizations,
  };
}
